"use client";

import { useEffect, useRef, useState } from "react";

const CodeEditorAnimation = ({ data }) => {
  const canvasRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions to match the container
    const updateCanvasSize = () => {
      const container = canvas.parentElement;
      if (container) {
        // Set a fixed aspect ratio to ensure content fits
        const width = Math.min(500, container.clientWidth);
        const height = Math.min(400, width * 0.8);

        canvas.width = width;
        canvas.height = height;

        // Update canvas style dimensions
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }
    };

    // Initial size update
    updateCanvasSize();

    // Listen for resize events
    window.addEventListener("resize", updateCanvasSize);

    // Colors
    const colors = {
      background: "#0f172a", // Darker background
      text: "#e2e8f0",
      comment: "#94a3b8",
      keyword: "#8b5cf6",
      string: "#10b981",
      function: "#3b82f6",
      variable: "#f59e0b",
      cursor: "#f8fafc",
    };

    

    // Animation variables
    let currentLine = 0;
    let currentChar = 0;
    let cursorVisible = true;
    let lastTime = 0;
    let cursorBlinkTime = 0;

    // Draw code editor
    const drawEditor = () => {
      if (!ctx) return;

      // Get current dimensions
      const width = canvas.width;
      const height = canvas.height;

      // Scale factors based on canvas size
      const scaleFactor = width / 500; // Base scale on a 500px reference width
      const lineHeight = 16 * scaleFactor;
      const startX = 40 * scaleFactor;
      const startY = 30 * scaleFactor;
      const tabSize = 20 * scaleFactor;
      const fontSize = 12 * scaleFactor;

      // Clear canvas
      ctx.fillStyle = colors.background;
      ctx.fillRect(0, 0, width, height);

      // Draw line numbers background
      ctx.fillStyle = "#0a0f1c"; // Even darker background for line numbers
      ctx.fillRect(0, 0, startX - 10 * scaleFactor, height);

      // Draw code lines
      for (
        let i = 0;
        i < Math.min(currentLine + 1, data?.codeLines.length);
        i++
      ) {
        // Safety check to prevent accessing beyond array bounds
        if (i >= data?.codeLines.length) continue;

        // Draw line number
        ctx.fillStyle = "#64748b";
        ctx.font = `${fontSize}px monospace`;
        ctx.textAlign = "right";
        ctx.fillText(
          `${i + 1}`,
          startX - 15 * scaleFactor,
          startY + i * lineHeight
        );

        // Draw code text
        if (i < currentLine && i < data?.codeLines.length) {
          // Completed lines
          ctx.fillStyle = colors[data?.codeLines[i]?.type];
          ctx.font = `${fontSize}px monospace`;
          ctx.textAlign = "left";

          // Handle indentation
          let indentLevel = 0;
          if (data?.codeLines[i].text.startsWith("  ")) {
            indentLevel =
              Math.floor(
                data?.codeLines[i].text.match(/^\s+/)?.[0].length || 0
              ) / 2;
          }

          ctx.fillText(
            data?.codeLines[i].text,
            startX + indentLevel * tabSize,
            startY + i * lineHeight
          );
        } else if (i === currentLine && i < data?.codeLines.length) {
          // Current line (typing)
          const currentText = data?.codeLines[i].text.substring(0, currentChar);
          ctx.fillStyle = colors[data?.codeLines[i]?.type];
          ctx.font = `${fontSize}px monospace`;
          ctx.textAlign = "left";

          // Handle indentation
          let indentLevel = 0;
          if (data?.codeLines[i].text.startsWith("  ")) {
            indentLevel =
              Math.floor(
                data?.codeLines[i].text.match(/^\s+/)?.[0].length || 0
              ) / 2;
          }

          ctx.fillText(
            currentText,
            startX + indentLevel * tabSize,
            startY + i * lineHeight
          );

          // Draw cursor
          if (cursorVisible) {
            const cursorX =
              startX +
              indentLevel * tabSize +
              ctx.measureText(currentText).width;
            ctx.fillStyle = colors.cursor;
            ctx.fillRect(
              cursorX,
              startY + i * lineHeight - lineHeight + 3 * scaleFactor,
              2 * scaleFactor,
              lineHeight
            );
          }
        }
      }
    };

    // Animation loop using requestAnimationFrame instead of setInterval
    const animate = (time) => {
      if (!isAnimating) return;

      // Calculate delta time
      const deltaTime = time - lastTime;
      lastTime = time;

      // Handle cursor blinking
      cursorBlinkTime += deltaTime;
      if (cursorBlinkTime > 500) {
        cursorVisible = !cursorVisible;
        cursorBlinkTime = 0;
      }

      // Handle typing (every 50ms)
      if (deltaTime > 10) {
        if (currentLine < data?.codeLines.length) {
          if (currentChar < data?.codeLines[currentLine].text.length) {
            currentChar++;
          } else {
            currentLine++;
            currentChar = 0;
          }
        } else {
          // Reset animation
          currentLine = 0;
          currentChar = 0;
        }
      }

      drawEditor();
      requestAnimationFrame(animate);
    };

    // Start animation
    drawEditor();
    requestAnimationFrame(animate);

    // Cleanup
    return () => {
      setIsAnimating(false);
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, [isAnimating, data?.codeLines]);

  return (
    <div className="relative w-full max-w-[700px]  mx-auto">
      <div className="absolute top-0 left-0 right-0 h-8 bg-slate-800 rounded-t-lg flex items-center px-4">
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className="text-xs text-slate-400 mx-auto">portfolio.tsx</div>
      </div>
      <canvas
        ref={canvasRef}
        className="rounded-lg pt-4 shadow-2xl border  border-slate-700 !w-full !h-[400px] 2xl:!h-[550px]"
        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      />
    </div>
  );
};

export default CodeEditorAnimation;
